import { useCallback } from 'react';
import { useIsFocused } from '@react-navigation/native';

import {
  type NotificationCategories,
  NotificationCategoryType,
  useNotificationPreferencesState,
} from '../../../hooks';
import { useLiveActivity } from '../../../integrations';
import { SUBSCRIPTION_TYPE } from '../constants';
import { useNotificationPermissions } from './useNotificationPermissions';
import { useNotificationPreferencesUpdates } from './useNotificationPreferencesUpdates';
import { useNotificationsTelemetry } from './useNotificationsTelemetry';

// ─────────────────────────────────────────────────────────────────────

export const useNotificationPreferences = (params: Params) => {
  const { failureMessage } = params;

  const isScreenFocused = useIsFocused();

  const state = useNotificationPreferencesState({
    requestPolicy: 'cache-and-network',
    pause: !isScreenFocused,
  });
  const permissions = useNotificationPermissions();
  const { onPreferenceChanged } = useNotificationsTelemetry();

  const { setNotificationPreferences, isUpdating } =
    useNotificationPreferencesUpdates({ failureMessage });
  const { cancelAllLiveActivities } = useLiveActivity();

  // ─────────────────────────────────────────────────────────────────────

  const mergeUpdatedPreferences = useCallback(
    (args: MergePreferencesParams) => {
      if (!state.preferences) return;

      const mergePreferences = mergePreferencesFactory(args);

      return state.preferences.reduce<NotificationCategories>(
        mergePreferences,
        [],
      );
    },
    [state.preferences],
  );

  const updateNotificationPreferences = useCallback(
    async (args: MergePreferencesParams) => {
      const updates = mergeUpdatedPreferences(args);

      if (!updates) return;

      const { category, subscriptionType, value } = args;

      const isOrderPushDisabled =
        category === NotificationCategoryType.OrderStatus &&
        subscriptionType === SUBSCRIPTION_TYPE.PUSH;

      const isSuccess = await setNotificationPreferences(updates);

      if (isSuccess) {
        onPreferenceChanged({
          category,
          value,
          channel:
            subscriptionType === SUBSCRIPTION_TYPE.PUSH ? 'push' : 'email',
        });
      }

      // NOTE: When the request succeeds and the user disables order-status push notifications,
      // we clear the current live activity from the device, since the user will no longer receive
      // live updates due to the updated notification preferences.
      if (isSuccess && isOrderPushDisabled) {
        cancelAllLiveActivities();
      }
    },
    [
      cancelAllLiveActivities,
      mergeUpdatedPreferences,
      onPreferenceChanged,
      setNotificationPreferences,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    preferences: state.preferences,
    error: state.error,
    isLoading: state.loading,
    refetch: state.refetch,
    unresolvedCategoriesNames: state.unresolvedCategoriesNames,
    permissions,
    isUpdating,
    updateNotificationPreferences,
  };
};

// ─── Helpers ──────────────────────────────────────────────────────────

function mergePreferencesFactory(args: MergePreferencesParams) {
  const { subscriptionType, category, value } = args;

  return function (
    categories: NotificationCategories,
    currentCategory: NotificationCategories[number],
  ): NotificationCategories {
    if (currentCategory.id !== category) {
      return [...categories, currentCategory];
    }

    return [...categories, { ...currentCategory, [subscriptionType]: value }];
  };
}

// ─── Types ───────────────────────────────────────────────────────────

type Params = {
  failureMessage: string;
};

type MergePreferencesParams = {
  category: string;
  subscriptionType: string;
  value: boolean;
};
